import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { KeyRound, Plus, Trash2, Copy, Menu, CheckCircle, Clock, FileDown } from "lucide-react";

export default function AdminInvites() {
  const { authAxios } = useAuth();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newRole, setNewRole] = useState("client");
  const [newNote, setNewNote] = useState("");
  const [creating, setCreating] = useState(false);

  const fetchCodes = useCallback(async () => {
    try {
      const res = await authAxios.get("/invite-codes");
      setCodes(res.data);
    } catch {
      toast.error("Errore nel caricamento dei codici");
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      await authAxios.post("/invite-codes", { role: newRole, note: newNote });
      toast.success("Codice invito creato");
      setDialogOpen(false);
      setNewNote("");
      fetchCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella creazione");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (codeId) => {
    try {
      await authAxios.delete(`/invite-codes/${codeId}`);
      toast.success("Codice eliminato");
      fetchCodes();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Codice copiato!");
  };

  const downloadInvitePdf = async (codeId) => {
    try {
      const res = await authAxios.get(`/invite-codes/${codeId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Invito_Triolo.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF scaricato!");
    } catch {
      toast.error("Errore nel download del PDF");
    }
  };

  const activeCodes = codes.filter((c) => !c.used);
  const usedCodes = codes.filter((c) => c.used);

  return (
    <div className="min-h-screen bg-[#09090b]">

      <main className="p-4 sm:p-6 lg:p-8">
        <PageHeader title="Codici Invito" />
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-1">Codici Invito</h1>
              <p className="text-zinc-400 text-sm sm:text-base">Gestisci gli accessi alla piattaforma</p>
            </div>
          </div>
          <Button
            data-testid="create-invite-btn"
            onClick={() => setDialogOpen(true)}
            className="bg-amber-500 hover:bg-amber-600 text-black"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuovo Codice
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <Card className="bg-[#18181b] border-white/5">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <KeyRound className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white font-mono">{codes.length}</p>
                <p className="text-xs text-zinc-400">Totale Codici</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#18181b] border-white/5">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <Clock className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-400 font-mono">{activeCodes.length}</p>
                <p className="text-xs text-zinc-400">Disponibili</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#18181b] border-white/5">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <CheckCircle className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-400 font-mono">{usedCodes.length}</p>
                <p className="text-xs text-zinc-400">Utilizzati</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Codes Table */}
        <Card className="bg-[#18181b] border-white/5">
          <CardHeader>
            <CardTitle className="text-white">Lista Codici</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500 mx-auto"></div>
              </div>
            ) : codes.length === 0 ? (
              <div className="p-8 text-center">
                <KeyRound className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-400">Nessun codice invito creato</p>
                <p className="text-zinc-500 text-sm mt-1">Crea il primo codice per permettere la registrazione</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead className="text-zinc-400 uppercase text-xs">Codice</TableHead>
                    <TableHead className="text-zinc-400 uppercase text-xs">Ruolo</TableHead>
                    <TableHead className="text-zinc-400 uppercase text-xs">Note</TableHead>
                    <TableHead className="text-zinc-400 uppercase text-xs">Stato</TableHead>
                    <TableHead className="text-zinc-400 uppercase text-xs">Usato da</TableHead>
                    <TableHead className="text-zinc-400 uppercase text-xs text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {codes.map((code) => (
                    <TableRow key={code.id} data-testid={`invite-row-${code.id}`} className="border-white/5">
                      <TableCell>
                        <span className="font-mono text-amber-400 font-medium tracking-wider">{code.code}</span>
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          code.role === "agent" ? "bg-blue-500/20 text-blue-400" :
                          code.role === "magazziniere" ? "bg-orange-500/20 text-orange-400" :
                          "bg-emerald-500/20 text-emerald-400"
                        }>
                          {code.role === "agent" ? "Agente" : code.role === "magazziniere" ? "Magazziniere" : "Cliente"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-zinc-400 text-sm">{code.note || "-"}</TableCell>
                      <TableCell>
                        {code.used ? (
                          <Badge className="bg-zinc-500/20 text-zinc-400">Utilizzato</Badge>
                        ) : (
                          <Badge className="bg-emerald-500/20 text-emerald-400">Disponibile</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-zinc-400 text-sm">{code.used_by_email || "-"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {!code.used && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                data-testid={`pdf-code-${code.id}`}
                                onClick={() => downloadInvitePdf(code.id)}
                                className="text-zinc-400 hover:text-blue-400"
                                title="Scarica PDF istruzioni"
                              >
                                <FileDown className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                data-testid={`copy-code-${code.id}`}
                                onClick={() => copyCode(code.code)}
                                className="text-zinc-400 hover:text-amber-400"
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`delete-code-${code.id}`}
                            onClick={() => handleDelete(code.id)}
                            className="text-zinc-400 hover:text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Create Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-[#18181b] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Nuovo Codice Invito</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Crea un codice per permettere la registrazione di un nuovo utente
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Tipo Utente *</Label>
                <Select value={newRole} onValueChange={setNewRole}>
                  <SelectTrigger data-testid="invite-role-select" className="bg-[#27272a] border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#27272a] border-white/10">
                    <SelectItem value="agent">Agente</SelectItem>
                    <SelectItem value="client">Cliente</SelectItem>
                    <SelectItem value="magazziniere">Magazziniere</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-zinc-500">
                  {newRole === "agent"
                    ? "Codice AG-XXXXXX - L'agente vedrà Listino + Prezzo Minimo"
                    : newRole === "magazziniere"
                    ? "Codice MG-XXXXXX - Il magazziniere gestisce giri di consegna e preparazione ordini"
                    : "Codice CL-XXXXXX - Il cliente vedrà solo il Listino e dovrà fornire la Partita IVA"}
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Note (opzionale)</Label>
                <Input
                  data-testid="invite-note-input"
                  type="text"
                  placeholder="Es: Per Mario Rossi - Ristorante Da Mario"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  className="bg-[#27272a] border-white/10"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-white/10">
                Annulla
              </Button>
              <Button
                data-testid="confirm-create-invite-btn"
                onClick={handleCreate}
                disabled={creating}
                className="bg-amber-500 hover:bg-amber-600 text-black"
              >
                {creating ? "Creazione..." : "Crea Codice"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
